import React from 'react'
import * as R from 'ramda'
import { t } from 'ttag'
import cx from 'classnames'

import { ActionsDropdown } from 'components/shared/ActionsDropdown/ActionsDropdown'
import { Button } from 'components/shared/Button/Button'
import { IconButton } from 'components/shared/IconButton/IconButton'
import { ActionsCell } from 'components/shared/Table/common/Cells/ActionsCell/ActionsCell'
import { CandidateCell } from 'components/shared/Table/common/Cells/CandidateCell/CandidateCell'
import { DateCell } from 'components/shared/Table/common/Cells/DateCell/DateCell'
import { JobProgressCell } from 'components/shared/Table/common/Cells/JobProgressCell/JobProgressCell'
import { PhoneCell } from 'components/shared/Table/common/Cells/PhoneCell/PhoneCell'
import { ScoreCell } from 'components/shared/Table/common/Cells/ScoreCell/ScoreCell'
import { TextCell } from 'components/shared/Table/common/Cells/TextCell/TextCell'
import { Table } from 'components/shared/Table/Table'
import { percentScore } from 'utils/stringFormat'
import { getTooltipMessage } from 'components/pages/Jobs/JobDetails/helpers'
import { useCandidatesTable } from 'components/pages/Jobs/JobDetails/hooks'
import { getColumns } from './model'
import { HIRE_STATUSES } from '../../../model'

import { FileTextIcon } from 'components/shared/Icon/model'

import { CandidatesTable } from '../../../CandidatesTable/CandidatesTable'

import styles from './InterviewsTable.module.scss'
import jobStyles from '../../../JobDetails.module.scss'

const mapSortingPropToQueryParam = {
  completed: 'completed',
  email: 'email',
  name: 'name',
  status: 'status',
  'Ebber Test Score': 'test_score',
}

export const InterviewsTable = ({
  projectId,
  search,
  filter,
  setFilter,
  tab,
  getActions,
  onInterview,
  onTestsReport,
  handleFavorite,
  hasInterviewModule,
  isInterviewAvailable,
  active,
}) => {
  const columns = getColumns()

  const status = React.useMemo(() => ['screened'], [])

  const {
    state: {
      table: {
        headers,
        model,
        rowSelection,
        isAllRowsSelected,
        isFetching,
        isSomeRowsSelected,
      },
      pagination: { currentPage, pageCount, perPage },
    },
    handlers: {
      table: { toggleAllRowsSelectedHandler },
      pagination: { handlePage, handlePageSize },
    },
  } = useCandidatesTable({
    columns,
    mapSortingPropToQueryParam,
    projectId,
    search,
    filter,
    tab,
    status,
  })

  return (
    <CandidatesTable
      {...{
        active,
        headers,
        isAllRowsSelected,
        isFetching,
        isSomeRowsSelected,
        currentPage,
        pageCount,
        perPage,
        toggleAllRowsSelectedHandler,
        handlePage,
        handlePageSize,
        filter,
        setFilter,
      }}
      placeholder={{
        title: t`No candidates in this stage`,
        text: t`No candidates are currently selected for interview`,
      }}
    >
      {R.map(
        ({
          id,
          original,
          getIsSelected,
          getIsSomeSelected,
          getToggleSelectedHandler,
        }) => {
          const {
            email,
            interview,
            name,
            phone,
            scores,
            selection_stage,
            status,
            timestamps,
            hire_status,
            is_favourite,
            id: candidateId,
          } = original

          const actions = getActions({ candidate: original })

          return (
            <Table.Row
              key={id}
              type="body"
              onChange={getToggleSelectedHandler()}
              isChecked={getIsSelected()}
              isIndeterminate={getIsSomeSelected()}
              isSelected={rowSelection[id]}
              isHighlighted={hire_status === HIRE_STATUSES.HIRED}
              hasCheckbox
              isDisabled={!active}
              className={jobStyles.row}
            >
              <CandidateCell
                email={email}
                name={name}
                className={active ? jobStyles.favoriteIcon : undefined}
                isFavorite={is_favourite}
                setFavorite={handleFavorite(candidateId)}
                active={active}
              />
              <TextCell text={percentScore(scores['GMA'])} />
              <TextCell text={percentScore(scores['Personality'])} />
              <ScoreCell score={scores['Ebber Test Score']} />
              <PhoneCell phone={phone} />
              <DateCell date={timestamps?.completed} hasNowLabel />
              <JobProgressCell
                progress={selection_stage - 1}
                status={status}
                type="candidate"
                {...{ hasInterviewModule }}
              />
              <ActionsCell>
                <Button
                  className={cx(styles.button, styles.startInterviewButton)}
                  color="primary"
                  label={t`Start interview`}
                  size="small"
                  variant="filled"
                  message={getTooltipMessage({
                    hasInterview: Boolean(interview),
                    isInterviewAvailable,
                  })}
                  onClick={() => onInterview({ id: interview })}
                  isDisabled={
                    !isInterviewAvailable ||
                    R.and(isInterviewAvailable, R.isNil(interview)) ||
                    !active
                  }
                />
                <IconButton
                  className={styles.button}
                  icon={FileTextIcon}
                  size="medium"
                  variant="text"
                  onClick={() => onTestsReport({ candidate: original })}
                />
                <ActionsDropdown
                  anchorClassName={styles.button}
                  size="medium"
                  isDisabled={!active}
                  {...{ actions }}
                />
              </ActionsCell>
            </Table.Row>
          )
        },
        model.rows,
      )}
    </CandidatesTable>
  )
}
